// frontend/src/app/components/projects/project-corpus/paper-row.component.ts
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, computed, signal } from '@angular/core';
import { missingMetadataFields } from '../../../project-corpus/domain/paper-status';
import { ImportSourceType, ProjectPaper } from '../../../project-corpus/domain/types';
import {
  labelForWorkType,
  labelForSourceType,
} from '../../../project-corpus/domain/openalex-types';
import { StatusBadgeComponent, StatusBadgeVariant } from './status-badge.component';

const SOURCE_LABELS: Record<ImportSourceType, string> = {
  OPENALEX: 'OpenAlex',
  BIBTEX: 'BibTeX',
  PDF: 'PDF',
  GROUP_CORPUS: 'Group corpus',
};

/**
 * Single paper row in the paper list.
 *
 * Responsive behaviour:
 *  - Mobile (default block layout): card with title on top, meta below,
 *    actions hidden behind a `…` button that toggles a small inline menu
 *    (Edit / Attach PDF). The menu is rendered with `md:hidden` so it
 *    never appears on desktop.
 *  - Desktop (`md:`): single-line row, the title + meta arrange inline,
 *    and the action buttons live on the right inside a `md:opacity-0
 *    md:group-hover:opacity-100` wrapper so they reveal on hover.
 *
 * Reuses the Phase-3 `StatusBadgeComponent` for the per-row status pill.
 */
@Component({
  selector: 'app-paper-row',
  standalone: true,
  imports: [CommonModule, StatusBadgeComponent],
  templateUrl: './paper-row.component.html',
  styleUrls: ['./paper-row.component.scss'],
})
export class PaperRowComponent {
  private readonly _paper = signal<ProjectPaper | null>(null);
  private readonly _readonly = signal(false);
  private readonly _importRunning = signal(false);
  private readonly _menuOpen = signal(false);

  @Input({ required: true })
  set paper(value: ProjectPaper) {
    this._paper.set(value);
  }
  get paper(): ProjectPaper {
    return this._paper() as ProjectPaper;
  }

  @Input()
  set readonly(value: boolean) {
    this._readonly.set(value);
  }
  get readonly(): boolean {
    return this._readonly();
  }

  @Input()
  set importRunning(value: boolean) {
    this._importRunning.set(value);
  }
  get importRunning(): boolean {
    return this._importRunning();
  }

  @Output() edit = new EventEmitter<ProjectPaper>();
  @Output() attach = new EventEmitter<ProjectPaper>();
  @Output() viewDetails = new EventEmitter<ProjectPaper>();
  @Output() viewAuditTrail = new EventEmitter<number>();

  readonly currentPaper = this._paper.asReadonly();
  readonly isReadonly = this._readonly.asReadonly();
  readonly menuOpen = this._menuOpen.asReadonly();

  readonly visibleStatus = computed<StatusBadgeVariant>(() => {
    const p = this._paper();
    if (!p) return 'COMPLETE';
    return this._importRunning() && p.status === 'NEEDS_METADATA' ? 'RUNNING' : p.status;
  });

  readonly sourceLabel = computed(() => {
    const p = this._paper();
    return p ? SOURCE_LABELS[p.source] : '';
  });

  /** Joins authors with commas, falls back to an em dash when missing. */
  readonly authorsLabel = computed(() => {
    const p = this._paper();
    if (!p || p.authors.length === 0) return '—';
    return p.authors.join(', ');
  });

  readonly workTypeLabel = computed(() => {
    const p = this._paper();
    return p?.workType ? labelForWorkType(p.workType) : null;
  });

  readonly sourceTypeLabel = computed(() => {
    const p = this._paper();
    return p?.sourceType ? labelForSourceType(p.sourceType) : null;
  });

  readonly externalIdUrl = computed(() => {
    const p = this._paper();
    if (!p?.externalId) return null;
    if (p.externalId.startsWith('http://') || p.externalId.startsWith('https://'))
      return p.externalId;
    return null;
  });

  readonly missingFieldsLabel = computed(() => {
    const p = this.currentPaper();
    if (this._importRunning()) return '';
    if (!p || p.status !== 'NEEDS_METADATA') return '';
    const fields = missingMetadataFields({
      title: p.title,
      abstractText: p.abstractText,
      authors: p.authors,
    });
    return fields.length ? `Missing: ${fields.join(', ')}` : '';
  });

  /** Compact meta line: "<first author et al.> · <year> · <venue>". */
  readonly metaLine = computed(() => {
    const p = this._paper();
    if (!p) return '';
    const parts: string[] = [];
    if (p.authors.length > 0) {
      const head = p.authors[0];
      parts.push(p.authors.length > 1 ? `${head} et al.` : head);
    }
    if (p.year !== null) parts.push(String(p.year));
    if (p.venue) parts.push(p.venue);
    return parts.join(' · ');
  });

  toggleMenu(): void {
    this._menuOpen.update((v) => !v);
  }

  onEdit(): void {
    this._menuOpen.set(false);
    const p = this._paper();
    if (p) this.edit.emit(p);
  }

  onAttach(): void {
    this._menuOpen.set(false);
    const p = this._paper();
    if (p) this.attach.emit(p);
  }

  onViewDetails(): void {
    this._menuOpen.set(false);
    const p = this._paper();
    if (p) this.viewDetails.emit(p);
  }

  onViewAuditTrail(): void {
    this._menuOpen.set(false);
    const p = this._paper();
    if (p) this.viewAuditTrail.emit(p.ptpId);
  }
}
